import { useMutation } from "@tanstack/react-query";
import httpClient from "@/services";
import { emitEvent, EventConstants } from "@/lib/event-emitter";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import type { IAccountResetResponse } from "./use-account-reset";
import type { AxiosError } from "axios";
import { QueryKeys } from "@/constants";
import { useNotificationError } from "@/context/error-provider";

export type IRegisterResponse = {
  groupId: string;
  firstname: string;
  lastname: string;
  password: string;
  zip?: string;
  dob: string;
  username: string;
  email: string;
  phone: string;
  ssnSuffix?: string;
  loginGroupId: string;
  divisionId: string;
  memberId?: string;
  employeeId?: string;
};
export type IVerifyOTPRegistration = {
  enteredOTP: string;
  signature: string;
} & IRegisterResponse;

export const registerUserDetails = async (
  registerResponse: IRegisterResponse,
) => {
  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: `/login/register`,
    headers: {
      "Accept-Version": "3.0",
      "Content-Type": "application/json",
    },
    data: registerResponse,
  };
  return httpClient.request(config).then((response) => {
    return response.data;
  });
};

export const verifyOTP = async (registerInfo: IVerifyOTPRegistration) => {
  const params = {
    firstname: registerInfo.firstname,
    lastname: registerInfo.lastname,
    password: registerInfo.password,
    zip: registerInfo.zip,
    dob: registerInfo.dob,
    username: registerInfo.username,
    email: registerInfo.email,
    phone: registerInfo.phone,
    ssnSuffix: registerInfo.ssnSuffix,
    groupId: registerInfo.groupId,
    loginGroupId: registerInfo.loginGroupId,
    divisionId: registerInfo.divisionId,
    employeeId: registerInfo.employeeId,
    enteredOTP: registerInfo.enteredOTP,
  };
  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: `/otp/registration`,
    headers: {
      "Accept-Version": "3.0",
      "Content-Type": "application/json",
      signature: `${registerInfo.signature}`,
    },
    data: params,
  };
  return httpClient.request(config).then((response) => {
    return response.data;
  });
};

const useMVerifyOTP = () => {
  return useMutation({
    mutationKey: [QueryKeys.REGISTER_OTP],
    mutationFn: (registerResponse: IVerifyOTPRegistration) => {
      return verifyOTP(registerResponse);
    },
    onSuccess: (data: IAccountResetResponse) => {
      if (!data.success) {
        emitEvent(EventConstants.ADD_ERROR, {
          message: data.message,
          type: ErrorNotificationType.ERROR,
        });
      } else {
        emitEvent(EventConstants.ADD_ERROR, {
          message: data.message,
          type: ErrorNotificationType.INFO,
        });
      }
    },
    onError: (error: AxiosError) => {
      emitEvent(EventConstants.ADD_ERROR, {
        message: error.message,
        type: ErrorNotificationType.ERROR,
      });
    },
  });
};

const useMRegister = () => {
  const { addError } = useNotificationError();
  return useMutation({
    mutationFn: (registerResponse: IRegisterResponse) => {
      return registerUserDetails(registerResponse);
    },
    mutationKey: [QueryKeys.REGISTER],

    onSuccess: (data: IAccountResetResponse) => {
      if (!data.success) {
        addError({
          message: data.message,
          type: ErrorNotificationType.ERROR,
        });
      } else {
        addError({
          message: data.message,
          type: ErrorNotificationType.INFO,
        });
      }
    },
    onError: (error: AxiosError) => {
      addError({
        message: error.message,
        type: ErrorNotificationType.ERROR,
      });
    },
  });
};

export { useMRegister, useMVerifyOTP };

{
  /* 
  | Use Case             | Use `useMutation`? |
| -------------------- | ------------------ |
| Add/edit/delete item | ✅ Yes              |
| Form submissions     | ✅ Yes              |
| Upload file          | ✅ Yes              |
| Fetch list of data   | ❌ Use `useQuery`   |
| staleTime            | ✅ No             |
| gcTime               | ✅ No              |

- do not use useMutation for fetching data and caching data

  */
}
